import {
  all,
  call,
  delay,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { change } from 'redux-form';
import { browserHistory } from 'react-router';
import moment from 'moment';
import { get } from 'lodash';

import * as apiCommon from '../../services/apiCommon';
import * as apiItem from '../../services/item/apiItem';
import * as apiItemLocation from '../../services/item/api_item_location';
import * as apiLabelPrint from '../../services/label_print/api_label_print';
import * as apiLocation from '../../services/location/apiLocation';

import * as docTypes from '../../constants/constant_doc_types';

import * as sagaCommon from '../sagasCommon';

import {
  FETCH_LIST_LABEL_PRINT,
  GET_LABEL_PRINT,
  PREVIEW_ITEM_LABEL_PRINT,
  PRINT_LABEL_PRINT_DONE,
  PRINT_LABEL_PRINT_ERROR,
  PRINT_LABEL_PRINT_INPROGRESS,
  PRINT_LABEL_PRINT,
  SAVE_LABEL_PRINT,
} from '../../constants/constant_saga_types';
import {
  IMMEDIATE_DOWNLOAD,
  IS_DONE_LOADING_PREVIEW,
  IS_ERROR_LOADING_PREVIEW,
  IS_LOADING_PREVIEW,
  LABEL_PRINT_FILE_DOWNLOAD,
} from '../../constants/constant_action_types';

export default function* (app) {
  return yield all([
    fork(deleteTaskSaga, app),
    fork(fetchListLabelPrintSaga, app),
    fork(getLabelPrintSaga, app),
    fork(labelPrintFileDownloadSaga, app),
    fork(previewItemLabelPrint, app),
    fork(printLabelPrintSaga, app),
    fork(saveLabelPrintSaga, app),
  ]);
}

export function* fetchListLabelPrintSaga(feathersApp) {
  yield takeLatest(
    FETCH_LIST_LABEL_PRINT,
    callFetchListLabelPrint,
    feathersApp
  );
}

function* callFetchListLabelPrint(feathersApp, action) {
  try {
    const { silent, filters } = action;
    yield sagaCommon.showProgressListTable(silent);

    const dataParams = {
      filters,
      list_type: docTypes.LABEL_PRINT,
    };

    const tableData = yield call(apiCommon.getDocs, feathersApp, dataParams);

    yield call(sagaCommon.doneListTable, {
      rows: tableData.data,
      total_rows: tableData.total,
    });
  } catch (e) {
    yield sagaCommon.showErrorListTable(e);
  }
}

export function* getLabelPrintSaga(feathersApp) {
  yield takeEvery(GET_LABEL_PRINT, callGetLabelPrint, feathersApp);
}

function* callGetLabelPrint(feathersApp, action) {
  try {
    yield sagaCommon.showProgressDataById();

    const { path, user } = yield select();
    const { id } = action;

    if (id) {
      const data = yield call(apiLabelPrint.fetchLabelPrintById, feathersApp, {
        id,
        path,
        user,
      });

      yield sagaCommon.doneDataById(data);
    }
  } catch (e) {
    yield sagaCommon.showErrorDataById(e, true);
  }
}

export function* saveLabelPrintSaga(feathersApp) {
  yield takeEvery(SAVE_LABEL_PRINT, callSaveLabelPrint, feathersApp);
}

function* callSaveLabelPrint(feathersApp, action) {
  try {
    yield sagaCommon.showProgressSaveRecord();

    const { tenant, user } = yield select();
    const params = { tenant, user, data: action.data };
    const result = yield call(
      apiLabelPrint.saveLabelPrint,
      feathersApp,
      params
    );

    const resultId = (result || {}).id;

    if ((result || {}).id) {
      browserHistory.push(`/admin/label_print/edit/${resultId}`);
      yield sagaCommon.doneSaveRecord(result, 'Label print was saved');
      yield sagaCommon.doneDataById(result);
    }
  } catch (e) {
    yield sagaCommon.showErrorSaveRecord(e, true);
  }
}

function* previewItemLabelPrint(feathersApp) {
  yield takeEvery(
    PREVIEW_ITEM_LABEL_PRINT,
    callPreviewItemLabelPrint,
    feathersApp
  );
}

function* callPreviewItemLabelPrint(feathersApp, action) {
  try {
    yield put({ type: IS_LOADING_PREVIEW });

    const { payload } = action;
    const { effectiveDate, itemId, locationId, printTemplateId } = payload;

    const item = yield call(apiItem.fetchItemById, feathersApp, { id: itemId });
    const location = yield call(apiLocation.fetchLocationById, feathersApp, { id: locationId });
    const locationType = get(location, 'location_type_id');
    const isSubLocation = get(location, 'is_sub_location');
    const locationCid = isSubLocation ? `${locationId}:${locationType}` : locationId;

    const availableFrom = moment(effectiveDate).startOf('day').utc().format();
    const itemLocation = yield call(
      apiItemLocation.getItemLocationByItemIdLocationId,
      feathersApp,
      {
        itemId,
        locationId: locationCid,
        available_from: availableFrom,
        include_tax_rate: true,
      },
    );

    const currencyId = itemLocation.currency_id;

    const itemGroupPriceRes = yield call(
      apiCommon.getDocs,
      feathersApp,
      {
        list_type: docTypes.ITEM_GROUP_PRICE,
        filters: {
          item: [itemId],
          currency: [currencyId],
          available_from: availableFrom,
          sorted_columns: {
            available_from: {
              direction: 'desc',
              position: 0,
            },
          },
        },
      },
    );

    const itemGroupPrice = itemGroupPriceRes.data.find(({ available_to }) => {
      const isActive = !available_to || moment().isSameOrBefore(available_to);
      return isActive;
    });

    itemLocation.location = location;
    itemLocation.item = item;
    itemLocation.item_group_price = {
      sale_price: 0,
      ...(itemGroupPrice || {}),
    };

    const previewPdfParams = {
      transaction: [{ data: itemLocation }],
      meta: { isCellsAutofill: true },
      templateId: printTemplateId,
    };

    const { data: base64Pdf } = yield call(apiCommon.previewPdf, feathersApp, previewPdfParams);
    yield put({ type: IS_DONE_LOADING_PREVIEW, payload: base64Pdf });
  } catch (error) {
    yield put({
      type: IS_ERROR_LOADING_PREVIEW,
      payload: error.message || error,
    });
  }
}

export function* printLabelPrintSaga(feathersApp) {
  yield takeLatest(PRINT_LABEL_PRINT, callPrintLabelPrint, feathersApp);
}

function* callPrintLabelPrint(feathersApp, action) {
  yield put({ type: PRINT_LABEL_PRINT_INPROGRESS });
  yield delay(500);

  try {
    const { payload } = action;
    const { referenceId, id } = payload;

    yield call(apiLabelPrint.printLabelPrint, feathersApp, id);
    yield put({ type: PRINT_LABEL_PRINT_DONE });
    yield put({ type: 'FETCH_RUN_HISTORY', reference_id: referenceId });

    browserHistory.push('/admin/job_status');

    yield take('FETCH_LIST_JOB');
    yield put(change('JobSearchForm', 'reference_id', referenceId));
  } catch (e) {
    yield put({ type: PRINT_LABEL_PRINT_ERROR, payload: e });
  }
}

export function* labelPrintFileDownloadSaga(app) {
  yield takeLatest(LABEL_PRINT_FILE_DOWNLOAD, callLabelPrintFileDownload, app);
}

function* callLabelPrintFileDownload(app, action) {
  try {
    const { payload: path } = action;
    const data = yield call(apiLabelPrint.labelPrintFileDownload, app, path);

    const fileName = path.split('/').pop().replace(/\.[^/.]+$/, '');
    const payload = {
      data,
      download: true,
      format: 'pdf',
      isBlob: true,
      mimeType: 'application/pdf',
      name: fileName,
    };

    yield put({ type: IMMEDIATE_DOWNLOAD, payload });
  } catch (err) {
    console.error('@callLabelPrintFileDownload:error', err);
  }
}

export function* deleteTaskSaga(feathersApp) {
  yield takeEvery('DELETE_LABEL_PRINT', callDeleteLabelPrint, feathersApp);
}

function* callDeleteLabelPrint(feathersApp, action) {
  yield put({ type: 'DELETE_RECORD_INPROGRESS' });
  try {
    const { id } = action;

    if (id) {
      const { tenant, user } = yield select();
      const params = { tenant, user, id };
      yield call(apiLabelPrint.deleteLabelPrint, feathersApp, params);
    }
    
    yield sagaCommon.doneDeleteRecord(id, 'task');
    browserHistory.push('/admin/label_print');
  } catch (error) {
    yield sagaCommon.showErrorDeleteRecord(error);
  }
}